import { motion } from "framer-motion";
import { Flower2, Heart, WheatOff } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const cardCls = "rounded-[1.75rem] border border-gold/50 bg-white/85 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.35)] backdrop-blur";
const reveal = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export function PriceTable({ title, rows, testId }) {
  return (
    <div className={`overflow-hidden ${cardCls}`} data-testid={testId}>
      <h3 className="border-b border-gold/30 bg-rose/60 px-7 py-5 font-serif text-2xl font-semibold text-burgundy">{title}</h3>
      <ul>
        {rows.map((r, i) => (
          <motion.li
            key={r.qty}
            initial={{ opacity: 0, x: -14 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04, duration: 0.45 }}
            className="flex items-center justify-between border-b border-gold/15 px-7 py-3.5 transition-colors last:border-0 hover:bg-blush/70"
            data-testid={`${testId}-row-${r.qty}`}
          >
            <span className="font-serif text-xl font-semibold text-wine">{r.qty} <span className="text-sm font-normal text-plum/50">cupcakes</span></span>
            <span className="font-serif text-xl font-semibold text-gold">{r.price}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

export function ExtrasCard({ extras }) {
  return (
    <motion.div {...reveal} transition={{ duration: 0.7 }} className={`overflow-hidden ${cardCls}`} data-testid="extras-card">
      <h3 className="flex items-center gap-2.5 border-b border-gold/30 bg-rose/60 px-7 py-5 font-serif text-2xl font-semibold text-burgundy">
        <Flower2 className="h-5 w-5 text-gold" /> Extras
      </h3>
      <ul>
        {extras.map((e) => (
          <li key={e.name} className="flex items-center justify-between gap-4 border-b border-gold/15 px-7 py-3.5 last:border-0" data-testid={`extra-${slug(e.name)}`}>
            <span className="text-sm sm:text-base text-plum/85">{e.name}</span>
            <span className="shrink-0 font-serif text-lg font-semibold text-gold">{e.note}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function Pill({ label, testId, premium }) {
  return (
    <span
      className={premium ? "rounded-full border border-burgundy/30 bg-white px-4 py-1.5 text-sm font-medium text-burgundy" : "rounded-full border border-gold/40 bg-blush px-4 py-1.5 text-sm text-wine"}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

export function FlavoursCard({ flavours, premium, premiumNote }) {
  return (
    <motion.div {...reveal} transition={{ duration: 0.7 }} className={`p-7 ${cardCls}`} data-testid="flavours-card">
      <h3 className="flex items-center gap-2.5 font-serif text-2xl font-semibold text-burgundy">
        <Heart className="h-5 w-5 text-gold" /> Cake Flavours
      </h3>
      <div className="mt-5 flex flex-wrap gap-2">
        {flavours.map((f) => <Pill key={f} label={f} testId={`flavour-${f.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>
      <div className="mt-5 border-t border-gold/20 pt-4">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-wine/60">{premiumNote}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {premium.map((f) => <Pill key={f} label={f} premium testId={`flavour-${f.toLowerCase().replace(/\s+/g, "-")}`} />)}
        </div>
      </div>
    </motion.div>
  );
}

export function DietaryCard({ dietary }) {
  return (
    <motion.div {...reveal} transition={{ duration: 0.7, delay: 0.08 }} className={`p-7 ${cardCls}`} data-testid="dietary-card">
      <h3 className="flex items-center gap-2.5 font-serif text-2xl font-semibold text-burgundy">
        <WheatOff className="h-5 w-5 text-gold" /> Also Available
      </h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {dietary.map((d) => <Pill key={d} label={d} testId={`dietary-${d.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>
      <p className="mt-3 text-xs italic text-plum/55">Available for an added price — just ask when you order.</p>
    </motion.div>
  );
}
